import { Chart, ChartDataset, LegendItem } from "chart.js";
import { IChartData } from "../../interfaces";
import { isPhaseSeries } from "../../utils/amplitude-phase";
import { getSeriesKey } from "../../utils/series-key";

interface ILineChartDataset extends ChartDataset<"line"> {
  parameterName?: string;
  idPoint?: number;
}

interface IBuildLegendOptions {
  exportMode?: boolean;
}

export interface IExtendedLegendItem extends LegendItem {
  pairedDatasetIndex?: number;
  pairedFillStyle?: string;
  yAxisID?: string;
  idPoint?: number;
  parameterName?: string;
}

const getExportLegendText = (parameterName?: string, units?: string, seriesName?: string): string => {
  if (!parameterName) {
    return seriesName ?? "";
  }

  if (!units) {
    return `${parameterName} — ${seriesName ?? ""}`;
  }

  return `${parameterName}, ${units} — ${seriesName ?? ""}`;
};

const getLegendGroupTitle = (parameterName?: string, units?: string): string =>
  [parameterName, units]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(", ");

export const buildChartLegendLabels = (
  chart: Chart<"line">,
  seriesData: IChartData[],
  colorBySeries?: Map<string, string>,
  options?: IBuildLegendOptions,
): IExtendedLegendItem[] => {
  const datasets = chart.data.datasets as ILineChartDataset[];

  const processedSeries = new Set<string>();

  const amplitudePhaseMap = new Map<string, { amplitude?: IChartData; phase?: IChartData }>();
  for (const item of seriesData) {
    if (!item.isAmplitudePhase || item.idPoint == null || !item.parameterName) continue;
    const key = getSeriesKey(item.idPoint, item.parameterName)!;
    const entry = amplitudePhaseMap.get(key) ?? {};
    if (isPhaseSeries(item)) entry.phase = item;
    else entry.amplitude = item;
    amplitudePhaseMap.set(key, entry);
  }

  const datasetIndexMap = new Map<string, number>();
  datasets.forEach((dataset, index) => {
    const key = `${dataset.label}|${dataset.yAxisID}|${dataset.parameterName}`;
    datasetIndexMap.set(key, index);
  });

  return seriesData.reduce<IExtendedLegendItem[]>((acc, series) => {
    if (series.isAmplitudePhase === true) {
      if (series.idPoint == null || !series.parameterName) {
        return acc;
      }

      const seriesKey = getSeriesKey(series.idPoint, series.parameterName)!;

      if (processedSeries.has(seriesKey)) {
        return acc;
      }

      const { amplitude, phase } = amplitudePhaseMap.get(seriesKey) ?? {};

      if (!amplitude && !phase) {
        return acc;
      }

      const amplitudeDatasetIndex = amplitude
        ? datasetIndexMap.get(`${amplitude.name}|${amplitude.units}|${amplitude.parameterName}`) ?? -1
        : -1;

      const phaseDatasetIndex = phase
        ? datasetIndexMap.get(`${phase.name}|${phase.units}|${phase.parameterName}`) ?? -1
        : -1;

      const datasetIndex = amplitudeDatasetIndex !== -1 ? amplitudeDatasetIndex : phaseDatasetIndex;

      if (datasetIndex === -1) {
        return acc;
      }

      const isVisible = chart.isDatasetVisible(datasetIndex);

      const color = colorBySeries?.get(seriesKey) ?? amplitude?.color ?? phase?.color;
      const hasPairInSameChart = amplitudeDatasetIndex !== -1 && phaseDatasetIndex !== -1;

      const displayName = amplitude?.name ?? phase?.name ?? series.name;
      const legendText = options?.exportMode
        ? getExportLegendText(
            series.parameterLabel ?? series.parameterName,
            amplitude && phase ? `${amplitude.units}/${phase.units}` : amplitude?.units ?? phase?.units ?? series.units,
            displayName,
          )
        : displayName;

      acc.push({
        text: legendText,
        fillStyle: color,
        strokeStyle: color,
        pairedFillStyle: hasPairInSameChart ? color : undefined,
        borderRadius: 6,
        pointStyle: "circle" as const,
        lineWidth: isVisible ? 0 : 1,
        hidden: !isVisible,
        datasetIndex,
        pairedDatasetIndex: hasPairInSameChart ? phaseDatasetIndex : undefined,
        yAxisID: getLegendGroupTitle(
          series.parameterLabel ?? series.parameterName,
          amplitude && phase ? `${amplitude.units}/${phase.units}` : amplitude?.units ?? phase?.units ?? series.units,
        ),
        idPoint: amplitude?.idPoint ?? phase?.idPoint,
        parameterName: series.parameterName,
      });

      processedSeries.add(seriesKey);

      return acc;
    }

    const datasetIndex = datasetIndexMap.get(`${series.name}|${series.units}|${series.parameterName}`) ?? -1;

    if (datasetIndex === -1) {
      return acc;
    }

    const isVisible = chart.isDatasetVisible(datasetIndex);

    const color = (datasets[datasetIndex]?.borderColor as string) ?? series.color;
    const legendText = options?.exportMode
      ? getExportLegendText(series.parameterLabel ?? series.parameterName, series.units, series.name)
      : series.name;

    acc.push({
      text: legendText,
      fillStyle: color,
      strokeStyle: color,
      borderRadius: 6,
      pointStyle: "circle" as const,
      lineWidth: isVisible ? 0 : 1,
      hidden: !isVisible,
      datasetIndex,
      yAxisID: getLegendGroupTitle(series.parameterLabel ?? series.parameterName, series.units),
      idPoint: series.idPoint,
      parameterName: series.parameterName,
    });

    return acc;
  }, []);
};
